import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import type { Device } from 'src/types/Device';
import type { DeviceUIList, DeviceUIDeviceSidePanelView, ViewCategory } from 'src/types/DeviceUI';
import { DeviceUIField } from 'src/types/DeviceUI';
import { devices } from 'src/stores/lists/devices';
import { device_ui } from 'src/stores/features/device-ui';
import {
	real_id,
	get_mode,
	isPill,
	isWallDisplay,
	isWeatherStation,
	isDistance,
	isFanDevice,
} from 'src/helpers/interface_helpers';
import { deviceViewOptionsRegistry } from './view-registry';
import {
	ADVANCED_ONLY_DEVICES,
	DEVICES_WITH_MODE_RESTRICTED_VIEW,
	DEVICES_WITH_SINGLE_VIEW,
} from 'src/constants';

export function getDeviceCategory(device: Device): ViewCategory {
	if (isFanDevice(device?.type)) return 'fan';

	if (device?.type === 'SHSW-25') {
		const status = readDeviceStatus(device?.id);
		const settings = readDeviceSettings(device?.id);
		if (get_mode(device, status, settings) === 'roller') return 'roller';
	}
	if (device?.category === 'light') return 'light';
	if (device?.category === 'relay') return 'relay';
	if (device?.category === 'roller') return 'roller';
	if (isPill(device?.type)) return 'pill_sensor';
	if (device?.gen === 'GBLE' && device?.category === 'sensor') return 'bluetooth_sensor';
	if (device?.category === 'sensor') return 'sensor';
	if (device?.category === 'power_meter') return 'power_meter';
	if (device?.category === 'emeter') return 'emeter';
	if (device?.category === 'inputs_reader') return 'inputs_reader';
	if (device?.category === 'remote_control') return 'remote_control';

	// default fallback
	return 'relay';
}

export function getAvailableViews(device: Device) {
	const category = getDeviceCategory(device);
	return deviceViewOptionsRegistry[category] ?? [];
}

/**
 * Returns true if the given device supports the "Change view" (Basic / Advanced)
 * side-panel view switching available in the new layout.
 *
 * Mirrors the `shouldHideViewSwitching` rules used by the per-device flow in
 * `device_settings_helpers.ts`, so the bulk command and the per-device entry
 * stay in sync.
 */
export function canChangeView(device: Device): boolean {
	if (!device || !device.type) return false;

	// Bulk command is meant for the base device; channels follow via
	// saveViewForAllChannels at submit time.
	if (typeof device.id === 'string' && device.id.includes('_')) return false;

	if (DEVICES_WITH_SINGLE_VIEW.includes(device.type)) return false;
	if (isWeatherStation(device.type)) return false;
	if (isDistance(device.type)) return false;

	const isExtractedDevice =
		isWallDisplay(device.type) &&
		(device.category === 'thermostat' || device.category === 'media_player');
	if (isExtractedDevice) return false;

	const restrictedModes = DEVICES_WITH_MODE_RESTRICTED_VIEW[device.type];
	if (restrictedModes) {
		const status = readDeviceStatus(device.id);
		const settings = readDeviceSettings(device.id);
		const mode = get_mode(device, status, settings);
		if (restrictedModes.includes(mode)) return false;
	}

	return true;
}

/**
 * Saves the chosen side-panel view for a device and all its channels.
 * Reads channels_count from the base device in the devices store to
 * build channel IDs (fb89b3, fb89b3_1, fb89b3_2, …).
 * Must be called AFTER executeModeReplacement / checkMode so the
 * devices store already contains the updated channels_count.
 */
export function saveViewForAllChannels(deviceId: string, view: DeviceUIDeviceSidePanelView): void {
	const baseId = real_id(deviceId);
	const allDevices = get(devices);
	const channelsCount = allDevices[baseId]?.channels_count ?? 1;

	for (let i = 0; i < channelsCount; i++) {
		const channelId = i === 0 ? baseId : `${baseId}_${i}`;
		device_ui.updateDeviceUIField(channelId, DeviceUIField.DEVICE_SIDE_PANEL_VIEW, view);
	}
}

export function getCurrentView(
	device: Device,
	deviceUIList: DeviceUIList = {},
): DeviceUIDeviceSidePanelView {
	const dui = deviceUIList?.[device?.id];
	const rawSaved = dui?.[DeviceUIField.DEVICE_SIDE_PANEL_VIEW];
	const saved: DeviceUIDeviceSidePanelView | undefined =
		isFanDevice(device?.type) &&
		rawSaved?.category === 'relay' &&
		(rawSaved.variant === 'basic' || rawSaved.variant === 'advanced')
			? { category: 'fan', variant: rawSaved.variant }
			: rawSaved;

	// Extracted wall display devices (thermostat or media player) should always use advanced view
	const isExtractedDevice =
		isWallDisplay(device?.type) &&
		(device?.category === 'thermostat' || device?.category === 'media_player');

	if (isExtractedDevice) {
		const category = getDeviceCategory(device);
		return {
			category,
			variant: 'advanced',
		};
	}

	const category = getDeviceCategory(device);

	const restrictedModes = DEVICES_WITH_MODE_RESTRICTED_VIEW[device?.type];
	if (restrictedModes) {
		const status = readDeviceStatus(device?.id);
		const settings = readDeviceSettings(device?.id);
		const mode = get_mode(device, status, settings);
		if (restrictedModes.includes(mode)) {
			return { category, variant: 'advanced' };
		}
	}

	const isSavedViewCompatible =
		!!saved &&
		saved.category === category &&
		!!deviceViewOptionsRegistry?.[category]?.find(
			(viewOption) => viewOption.variant === saved.variant,
		);
	if (isSavedViewCompatible) return saved;

	const defaultVariant: 'basic' | 'advanced' = ADVANCED_ONLY_DEVICES.includes(device?.type)
		? 'advanced'
		: 'basic';

	return {
		category,
		variant: defaultVariant,
	};
}
